// src/inviteApply.js

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";

import { db } from "./firebase";
import { isFacilityInvitationRole, registerFacilityAccount } from "./services/access/facilityInvitations";
import { hasApprovedWorkerReadiness } from "./core/workforce/workerReadiness";

import {
  PERMISSIONS,
  PLATFORM_ROLES,
  normalizeId,
  normalizePlatformRole,
} from "./security/permissions";

import {
  finalizeFacilityMembership,
} from "./finalizeFacilityMembership";

/*
 * ShiftBridge Canonical Invite Application
 *
 * PURPOSE
 * -------
 * Convert an authorized ShiftBridge invitation into the correct
 * account type without mixing facility accounts into the worker
 * onboarding lifecycle.
 *
 * EXISTING ACCOUNT REPAIR
 * -----------------------
 * For an existing user, the canonical lookup order is:
 *
 *   users/{uid}.inviteId
 *        ↓
 *   invites/{inviteId} direct GET
 *
 * This avoids requiring a global invites LIST query merely to repair
 * an already-authenticated Planner or facility account.
 *
 * Brand-new accounts still retain the email-document lookup and the
 * legacy generated-ID email-query fallback.
 */

/* =========================================================
   NORMALIZATION
   ========================================================= */

function normEmail(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normId(value) {
  return normalizeId(value);
}

function roleKey(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");
}

function uniqueIds(values = []) {
  return Array.from(
    new Set(
      values
        .map(normId)
        .filter(Boolean)
        .filter((value) => value !== "all")
    )
  );
}

/*
 * =========================================================
 * INVITE AUTHORIZATION OVERRIDES
 * =========================================================
 *
 * Do not blindly copy an arbitrary permissions object from
 * an invitation.
 *
 * At this stage the only invite-level operational override
 * is facility visibility for an internal administrator.
 */

function normalizeInvitePermissionOverrides(
  invite = {}
) {
  const source =
    invite?.permissions &&
    typeof invite.permissions === "object" &&
    !Array.isArray(invite.permissions)
      ? invite.permissions
      : {};

  const result = {};

  const canViewAllFacilities =
    source[
      PERMISSIONS.CAN_VIEW_ALL_FACILITIES
    ];

  if (
    typeof canViewAllFacilities ===
    "boolean"
  ) {
    result[
      PERMISSIONS.CAN_VIEW_ALL_FACILITIES
    ] = canViewAllFacilities;
  }

  return result;
}


function normalizeInviteAccessScope(
  invite = {}
) {
  const value =
    String(
      invite?.accessScope || ""
    )
      .trim()
      .toLowerCase();

  return [
    "all_facilities",
    "selected_facilities",
  ].includes(value)
    ? value
    : "";
}

/*
 * =========================================================
 * CANONICAL FACILITY SCOPE BUILDER
 * =========================================================
 *
 * Every provisioning path must produce the same four
 * facility fields:
 *
 *   facilityId
 *   primaryFacilityId
 *   facilityIds
 *   allowedFacilityIds
 *
 * The primary facility must always be represented in
 * both facility arrays.
 */

function buildCanonicalFacilityScope({
  facilityId = "",
  primaryFacilityId = "",
  facilityIds = [],
  allowedFacilityIds = [],
} = {}) {
  const normalizedFacilityIds =
    uniqueIds([
      ...(Array.isArray(facilityIds)
        ? facilityIds
        : facilityIds
        ? [facilityIds]
        : []),

      ...(Array.isArray(
        allowedFacilityIds
      )
        ? allowedFacilityIds
        : allowedFacilityIds
        ? [allowedFacilityIds]
        : []),

      facilityId,
      primaryFacilityId,
    ]);

  const resolvedPrimaryFacilityId =
    normId(
      primaryFacilityId ||
        facilityId
    ) ||
    normalizedFacilityIds[0] ||
    "";

  const finalFacilityIds =
    uniqueIds([
      resolvedPrimaryFacilityId,
      ...normalizedFacilityIds,
    ]);

  return {
    facilityId:
      resolvedPrimaryFacilityId,

    primaryFacilityId:
      resolvedPrimaryFacilityId,

    facilityIds:
      finalFacilityIds,

    allowedFacilityIds:
      finalFacilityIds,
  };
}

/* =========================================================
   INVITE STATUS
   ========================================================= */

function getInviteStatus(invite = {}) {
  return String(
    invite?.status ||
      invite?.invitationStatus ||
      invite?.inviteStatus ||
      "sent"
  )
    .trim()
    .toLowerCase();
}

function inviteIsBlocked(invite = {}) {
  const status =
    getInviteStatus(invite);

  return (
    [
      "revoked",
      "cancelled",
      "canceled",
      "expired",
      "deleted",
    ].includes(status) ||
    invite?.disabled === true ||
    invite?.active === false ||
    invite?.archived === true
  );
}

/* =========================================================
   ROLE CLASSIFICATION
   ========================================================= */

function isWorkerRole(role) {
  return (
    normalizePlatformRole(role) ===
    PLATFORM_ROLES.WORKER
  );
}

function isPlannerRole(role) {
  return (
    normalizePlatformRole(role) ===
    PLATFORM_ROLES.FACILITY_PLANNER
  );
}

function isFacilityViewerRole(role) {
  const normalized =
    normalizePlatformRole(role);

  return [
    PLATFORM_ROLES.FACILITY_CLIENT,
    PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,
  ].includes(normalized);
}

function isFacilityOperationalRole(role) {
  return (
    isPlannerRole(role) ||
    isFacilityViewerRole(role)
  );
}

function requiresFacilityScope(role) {
  /*
   * Facility accounts require a facility immediately
   * because their entire operational identity is
   * facility-scoped.
   *
   * Workers are different:
   *
   * A worker may register, enter onboarding, and complete
   * compliance before a specific facility assignment is
   * established.
   *
   * Worker operational facility access is finalized later
   * when an actual facility assignment exists.
   */
  return isFacilityOperationalRole(role);
}

/* =========================================================
   CREDENTIAL NORMALIZATION
   ========================================================= */

function normalizeInviteCredentials(
  invite
) {
  const inviteCredentials =
    Array.isArray(invite?.credentials)
      ? invite.credentials
      : [];

  return inviteCredentials
    .map((credential) => {
      if (
        typeof credential === "string"
      ) {
        const type =
          roleKey(credential);

        return type
          ? {
              type,
              verified: true,
              expiresAt: null,
            }
          : null;
      }

      if (
        credential &&
        typeof credential === "object"
      ) {
        const type = roleKey(
          credential.type ||
            credential.credential ||
            ""
        );

        if (!type) {
          return null;
        }

        return {
          type,
          verified:
            credential.verified === true,
          expiresAt:
            credential.expiresAt ?? null,
        };
      }

      return null;
    })
    .filter(Boolean);
}

/* =========================================================
   FACILITY SCOPE
   ========================================================= */

function normalizeInviteFacilityScope(
  invite = {}
) {
  return buildCanonicalFacilityScope({
    facilityId:
      invite?.facilityId,

    primaryFacilityId:
      invite?.primaryFacilityId,

    facilityIds:
      invite?.facilityIds,

    allowedFacilityIds:
      invite?.allowedFacilityIds,
  });
}

function normalizeExistingFacilityScope(
  existing = {}
) {
  return buildCanonicalFacilityScope({
    facilityId:
      existing?.facilityId,

    primaryFacilityId:
      existing?.primaryFacilityId,

    facilityIds: [
      ...(Array.isArray(
        existing?.facilityIds
      )
        ? existing.facilityIds
        : []),

      ...(Array.isArray(
        existing?.assignedFacilityIds
      )
        ? existing.assignedFacilityIds
        : []),
    ],

    allowedFacilityIds:
      existing?.allowedFacilityIds,
  });
}

/* =========================================================
   INVITE LOOKUP
   ========================================================= */

async function loadInviteByExactId(
  inviteId,
  emailLower
) {
  const cleanInviteId =
    String(inviteId || "").trim();

  if (!cleanInviteId) {
    return null;
  }

  console.log(
    "INVITE LOOKUP STAGE A — EXACT INVITE ID START",
    {
      inviteId: cleanInviteId,
      email: emailLower,
    }
  );

  try {
    const inviteRef = doc(
      db,
      "invites",
      cleanInviteId
    );

    const inviteSnap =
      await getDoc(inviteRef);

    if (!inviteSnap.exists()) {
      console.warn(
        "INVITE LOOKUP STAGE A — EXACT INVITE ID NOT FOUND",
        {
          inviteId: cleanInviteId,
        }
      );

      return null;
    }

    const invite =
      inviteSnap.data() || {};

    const inviteEmail =
      normEmail(
        invite?.emailLower ||
          invite?.email ||
          ""
      );

    if (
      inviteEmail &&
      emailLower &&
      inviteEmail !== emailLower
    ) {
      console.error(
        "INVITE LOOKUP STAGE A — EXACT INVITE EMAIL MISMATCH",
        {
          inviteId: cleanInviteId,
          inviteEmail,
          authenticatedEmail:
            emailLower,
        }
      );

      return null;
    }

    console.log(
      "INVITE LOOKUP STAGE A — EXACT INVITE ID SUCCESS",
      {
        inviteId: inviteSnap.id,
      }
    );

    return {
      inviteDocId:
        inviteSnap.id,
      invite,
    };
  } catch (error) {
    console.error(
      "INVITE LOOKUP STAGE A — EXACT INVITE ID FAILED",
      {
        inviteId: cleanInviteId,
        code: error?.code,
        message: error?.message,
      }
    );

    throw error;
  }
}

async function loadInviteByEmailDocument(
  emailLower
) {
  console.log(
    "INVITE LOOKUP STAGE B — EMAIL DOCUMENT START",
    {
      email: emailLower,
    }
  );

  try {
    const directRef = doc(
      db,
      "invites",
      emailLower
    );

    const directSnap =
      await getDoc(directRef);

    if (!directSnap.exists()) {
      console.log(
        "INVITE LOOKUP STAGE B — EMAIL DOCUMENT NOT FOUND"
      );

      return null;
    }

    console.log(
      "INVITE LOOKUP STAGE B — EMAIL DOCUMENT SUCCESS",
      {
        inviteId:
          directSnap.id,
      }
    );

    return {
      inviteDocId:
        directSnap.id,
      invite:
        directSnap.data() || {},
    };
  } catch (error) {
    console.error(
      "INVITE LOOKUP STAGE B — EMAIL DOCUMENT FAILED",
      {
        code: error?.code,
        message: error?.message,
      }
    );

    throw error;
  }
}

async function loadInviteByLegacyEmailQuery(
  emailLower
) {
  console.log(
    "INVITE LOOKUP STAGE C — LEGACY EMAIL QUERY START",
    {
      email: emailLower,
    }
  );

  try {
    const inviteQuery = query(
      collection(db, "invites"),
      where(
        "email",
        "==",
        emailLower
      )
    );

    const snapshot =
      await getDocs(inviteQuery);

    if (snapshot.empty) {
      console.log(
        "INVITE LOOKUP STAGE C — LEGACY EMAIL QUERY EMPTY"
      );

      return null;
    }

    const inviteDoc =
      snapshot.docs[0];

    console.log(
      "INVITE LOOKUP STAGE C — LEGACY EMAIL QUERY SUCCESS",
      {
        inviteId:
          inviteDoc.id,
      }
    );

    return {
      inviteDocId:
        inviteDoc.id,
      invite:
        inviteDoc.data() || {},
    };
  } catch (error) {
    console.error(
      "INVITE LOOKUP STAGE C — LEGACY EMAIL QUERY FAILED",
      {
        code: error?.code,
        message: error?.message,
      }
    );

    throw error;
  }
}

async function loadInviteForAccount({
  emailLower,
  existing = {},
}) {
  /*
   * EXISTING ACCOUNT REPAIR
   *
   * The user's own profile is already readable. If that profile carries
   * inviteId, use it first. This avoids a global invites LIST operation.
   */
  const existingInviteId =
    String(
      existing?.inviteId || ""
    ).trim();

  if (existingInviteId) {
    const exactResult =
      await loadInviteByExactId(
        existingInviteId,
        emailLower
      );

    if (exactResult) {
      return exactResult;
    }
  }

  /*
   * Canonical newer invitations commonly use the lowercase email
   * as the invite document ID.
   */
  const emailDocResult =
    await loadInviteByEmailDocument(
      emailLower
    );

  if (emailDocResult) {
    return emailDocResult;
  }

  /*
   * Legacy generated-ID fallback.
   *
   * This remains for older/brand-new accounts only. Existing accounts
   * with users/{uid}.inviteId should never need this path.
   */
  return loadInviteByLegacyEmailQuery(
    emailLower
  );
}

/* =========================================================
   ACCOUNT STATE BUILDERS
   ========================================================= */

function buildWorkerState() {
  return {
    status:
      "pending_registration_approval",

    registrationStatus:
      "pending",

    registrationApproved:
      false,

    registrationApprovedAt:
      null,

    registrationApprovedBy:
      "",

    onboardingStatus:
      "locked",

    onboardingCompleted:
      false,

    workerStatus:
      "registration_pending",

    workerOperationalState:
      "REGISTRATION_PENDING",

    complianceStatus:
      "not_started",

    canAccessOnboarding:
      false,

    canAccessDashboard:
      false,

    profileCompleted:
      false,

    documentsSubmitted:
      false,

    documentsApproved:
      false,

    readyForShifts:
      false,

    approvedAt: null,
    approvedBy: "",

    submittedAt: null,

    documentsApprovedAt:
      null,

    documentsApprovedBy:
      "",
  };
}

function buildFacilityAccountState() {
  return {
    status: "active",

    registrationStatus:
      "not_applicable",

    registrationApproved:
      true,

    onboardingStatus:
      "not_applicable",

    onboardingCompleted:
      true,

    workerStatus:
      "not_applicable",

    workerOperationalState:
      "NOT_APPLICABLE",

    complianceStatus:
      "not_applicable",

    canAccessOnboarding:
      false,

    canAccessDashboard:
      true,

    profileCompleted:
      true,

    documentsSubmitted:
      false,

    documentsApproved:
      false,

    readyForShifts:
      false,
  };
}

function buildOtherAccountState() {
  return {
    status: "active",

    registrationStatus:
      "not_applicable",

    registrationApproved:
      true,

    onboardingStatus:
      "not_applicable",

    onboardingCompleted:
      true,

    workerStatus:
      "not_applicable",

    workerOperationalState:
      "NOT_APPLICABLE",

    complianceStatus:
      "not_applicable",

    canAccessOnboarding:
      false,

    canAccessDashboard:
      true,
  };
}

function getAccountState(role) {
  if (isWorkerRole(role)) {
    return buildWorkerState();
  }

  if (
    isFacilityOperationalRole(role)
  ) {
    return buildFacilityAccountState();
  }

  return buildOtherAccountState();
}

/* =========================================================
   INVITE CLAIM
   ========================================================= */

async function markInviteRegistered({
  inviteDocId,
  uid,
  role,
  tenantId,
  facilityId,
  facilityIds,
}) {
  await setDoc(
    doc(
      db,
      "invites",
      inviteDocId
    ),
    {
      status: "registered",
      inviteStatus: "registered",
      invitationStatus:
        "registered",

      claimedByUid: uid,

      registeredRole: role,

      tenantId,

      facilityId:
        facilityId || null,

      facilityIds,

      claimedAt:
        serverTimestamp(),

      registeredAt:
        serverTimestamp(),

      updatedAt:
        serverTimestamp(),
    },
    { merge: true }
  );
}

/* =========================================================
   ACCESS FINALIZATION
   ========================================================= */

async function finalizeOperationalAccess({
  inviteStatus,

  uid,
  email,

  role,

  tenantId,

  facilityId,
  facilityIds,

  credentials,

  inviteDocId,

  existing = {},
}) {
  if (
    isFacilityOperationalRole(role)
  ) {
    return finalizeFacilityMembership({
      uid,
      email,

      role,
      platformRole: role,

      tenantId,

      status: "active",

      facilityId,
      primaryFacilityId:
        facilityId,

      facilityIds,
      allowedFacilityIds:
        facilityIds,

      credentials,

      disabled: false,

      inviteId:
        inviteDocId,
    });
  }

  if (isWorkerRole(role)) {
    const worker = await getDoc(doc(db, "users", uid));
    if (!worker.exists() || !hasApprovedWorkerReadiness(worker.data())) {
      return null;
    }

/*
 * Approval alone does not create operational facility
 * access for a worker.
 *
 * A worker may legitimately be approved while still
 * waiting for a facility assignment.
 *
 * accessProfiles/{uid} and the facility roster mirror
 * are created only once a concrete facility exists.
 */
if (
  !facilityId ||
  !Array.isArray(facilityIds) ||
  facilityIds.length === 0
) {
  return null;
}

return finalizeFacilityMembership({
      uid,
      email,

      role,
      platformRole: role,

      tenantId,

      status: "active",

      facilityId,
      primaryFacilityId:
        facilityId,

      facilityIds,
      allowedFacilityIds:
        facilityIds,

      credentials,

      disabled: false,

      inviteId:
        inviteDocId,
    });
  }

  return null;
}

/* =========================================================
   PUBLIC API
   ========================================================= */

// Registration is an identity operation, not operational-access provisioning.
// Commit the profile and invitation claim together so the pipeline cannot see
// a half-registered worker. A retry must never reset an existing worker's state.
export async function registerInvitedWorker(user, inviteDocId) {
  const uid = String(user?.uid || "").trim();
  const email = normEmail(user?.email);
  if (!uid || !email || !inviteDocId) {
    throw new Error("An authenticated invited Worker is required.");
  }
  const userRef = doc(db, "users", uid);
  const inviteRef = doc(db, "invites", inviteDocId);
  return runTransaction(db, async (transaction) => {
    const inviteSnap = await transaction.get(inviteRef);
    const userSnap = await transaction.get(userRef);
    const invite = inviteSnap.exists() ? inviteSnap.data() : null;
    if (!invite || inviteIsBlocked(invite) ||
        normEmail(invite.emailLower || invite.email) !== email ||
        !isWorkerRole(invite.platformRole || invite.role) ||
        (invite.claimedByUid && invite.claimedByUid !== uid)) {
      throw new Error("This Worker invitation is not available for this account.");
    }
    if (userSnap.exists()) {
      const existing = userSnap.data();
      if (existing.inviteId === inviteDocId &&
          existing.tenantId === invite.tenantId &&
          isWorkerRole(existing.platformRole || existing.role) &&
          invite.claimedByUid === uid && getInviteStatus(invite) === "registered") {
        return { applied: true, inviteId: inviteDocId, role: "worker" };
      }
      throw new Error("An existing profile requires administrator review.");
    }
    if (!["sent", "invited", "pending"].includes(getInviteStatus(invite))) {
      throw new Error("This invitation is no longer awaiting registration.");
    }
    const tenantId = normId(invite.tenantId);
    if (!tenantId) throw new Error("The invitation is missing its tenant.");
    const scope = normalizeInviteFacilityScope(invite);
    const name = String(invite.name || invite.displayName || "").trim();
    const position = String(invite.primaryRole || invite.position || invite.workerType || "").trim();
    transaction.set(userRef, {
      uid, email, emailLower: email, tenantId, inviteId: inviteDocId,
      role: "worker", platformRole: "worker", accountType: "worker",
      name, displayName: name, phone: String(invite.phone || "").trim(),
      primaryRole: position, position, workerType: position,
      ...scope,
      ...buildWorkerState(),
      disabled: false, archived: false,
      credentialMap: {},
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    transaction.update(inviteRef, {
      status: "registered", inviteStatus: "registered",
      invitationStatus: "registered", registrationStatus: "registered",
      claimedByUid: uid, registeredRole: "worker",
      claimedAt: serverTimestamp(), registeredAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { applied: true, inviteId: inviteDocId, role: "worker",
      tenantId, facilityId: scope.facilityId, facilityIds: scope.facilityIds,
      operationalAccessProvisioned: false };
  });
}

export async function applyInviteIfExists(
  user
) {
  const emailLower =
    normEmail(user?.email);

  const uid =
    String(user?.uid || "").trim();

  if (!emailLower || !uid) {
    return {
      applied: false,
      reason: "no-user",
    };
  }

  /* =====================================================
     1. LOAD EXISTING USER FIRST
     ===================================================== */

  const userRef = doc(
    db,
    "users",
    uid
  );

  console.log(
    "INVITE LOOKUP — USER PROFILE START",
    {
      uid,
      email: emailLower,
    }
  );

  let userSnap;

  try {
    userSnap =
      await getDoc(userRef);

    console.log(
      "INVITE LOOKUP — USER PROFILE SUCCESS",
      {
        exists:
          userSnap.exists(),
        inviteId:
          userSnap.exists()
            ? String(
                userSnap.data()
                  ?.inviteId || ""
              )
            : "",
      }
    );
  } catch (error) {
    console.error(
      "INVITE LOOKUP — USER PROFILE FAILED",
      {
        code: error?.code,
        message: error?.message,
      }
    );

    throw error;
  }

  const existing =
    userSnap.exists()
      ? userSnap.data() || {}
      : {};

  /* =====================================================
     2. LOAD INVITE USING TRUSTED DIRECT ID FIRST
     ===================================================== */

  const {
    inviteDocId,
    invite,
  } =
    (await loadInviteForAccount({
      emailLower,
      existing,
    })) || {};

  if (
    !inviteDocId ||
    !invite
  ) {
    return {
      applied: false,
      reason: "no-invite",
    };
  }

  if (inviteIsBlocked(invite)) {
    return {
      applied: false,
      reason: "invite-blocked",
    };
  }

  const inviteStatus =
    getInviteStatus(invite);

  /* =====================================================
     3. CLAIM SAFETY
     ===================================================== */

  const previouslyClaimedUid =
    String(
      invite?.claimedByUid || ""
    ).trim();

  if (
    previouslyClaimedUid &&
    previouslyClaimedUid !== uid
  ) {
    console.error(
      "Invite claim rejected — invite already belongs to another UID.",
      {
        email: emailLower,
        inviteDocId,
        previouslyClaimedUid,
        attemptedUid: uid,
      }
    );

    return {
      applied: false,
      reason:
        "invite-already-claimed",
    };
  }

  /* =====================================================
     4. NORMALIZE ROLE / TENANT / FACILITY
     ===================================================== */

  const inviteRole =
    normalizePlatformRole(
      invite?.platformRole ||
        invite?.role ||
        "worker"
    );

  if (isWorkerRole(inviteRole) && !userSnap.exists()) {
    return registerInvitedWorker(user, inviteDocId);
  }

  if (isFacilityInvitationRole(inviteRole)) {
    return registerFacilityAccount(user, inviteDocId);
  }

/*
 * Tenant authorization must use a canonical tenant ID.
 *
 * tenantName is display information and must never
 * become an operational/security identifier.
 *
 * tenantKey remains temporarily supported for legacy
 * ShiftBridge records during migration.
 */
const inviteTenantId =
  normId(
    invite?.tenantId ||
      invite?.tenantKey ||
      ""
  );

  const inviteFacilityScope =
    normalizeInviteFacilityScope(
      invite
    );

  if (!inviteRole) {
    return {
      applied: false,
      reason:
        "invite-missing-role",
    };
  }

  if (!inviteTenantId) {
    return {
      applied: false,
      reason:
        "invite-missing-tenant",
    };
  }

  if (
    requiresFacilityScope(
      inviteRole
    ) &&
    !inviteFacilityScope.facilityId
  ) {
    return {
      applied: false,
      reason:
        "invite-missing-facility",
    };
  }

 const credentials =
  normalizeInviteCredentials(
    invite
  );

const inviteAccessScope =
  normalizeInviteAccessScope(
    invite
  );

const invitePermissionOverrides =
  normalizeInvitePermissionOverrides(
    invite
  );

const existingRole =
    normalizePlatformRole(
      existing?.platformRole ||
        existing?.role ||
        ""
    );

const existingTenantId =
  normId(
    existing?.tenantId ||
      existing?.tenantKey ||
      ""
  );

  if (
    existingTenantId &&
    existingTenantId !==
      inviteTenantId
  ) {
    console.error(
      "Invite application rejected — tenant mismatch.",
      {
        uid,
        email: emailLower,
        existingTenantId,
        inviteTenantId,
      }
    );

    return {
      applied: false,
      reason:
        "tenant-mismatch",
    };
  }

  if (
    existingRole &&
    existingRole !== inviteRole
  ) {
    console.error(
      "Invite application rejected — role mismatch.",
      {
        uid,
        email: emailLower,
        existingRole,
        inviteRole,
      }
    );

    return {
      applied: false,
      reason:
        "role-mismatch",
    };
  }

  const existingFacilityScope =
    normalizeExistingFacilityScope(
      existing
    );

 const finalFacilityScope =
  buildCanonicalFacilityScope({
    /*
     * Preserve an existing valid primary facility.
     * For a new account, the invitation's primary
     * facility becomes authoritative.
     */
    primaryFacilityId:
      existingFacilityScope.primaryFacilityId ||
      inviteFacilityScope.primaryFacilityId,

    facilityIds: [
      ...existingFacilityScope.facilityIds,
      ...inviteFacilityScope.facilityIds,
    ],

    allowedFacilityIds: [
      ...existingFacilityScope.allowedFacilityIds,
      ...inviteFacilityScope.allowedFacilityIds,
    ],
  });

const finalFacilityId =
  finalFacilityScope.facilityId;

const finalFacilityIds =
  finalFacilityScope.facilityIds;

  if (
    requiresFacilityScope(
      inviteRole
    ) &&
    !finalFacilityId
  ) {
    return {
      applied: false,
      reason:
        "account-missing-facility",
    };
  }

  const existingCredentials =
    Array.isArray(
      existing?.credentials
    )
      ? existing.credentials
      : [];

  const finalCredentials =
    existingCredentials.length > 0
      ? existingCredentials
      : credentials;

  /* =====================================================
     5. WRITE CANONICAL USER PROFILE
     ===================================================== */

  const accountState =
    getAccountState(
      inviteRole
    );

  const userPayload = {
    uid,

    email: emailLower,
    emailLower,

    role: inviteRole,
    platformRole:
      inviteRole,

 tenantId:
  inviteTenantId,

inviteId:
  inviteDocId,

/*
 * Internal-admin operational scope.
 *
 * Facility operational accounts later receive their
 * authorization from accessProfiles/{uid}; internal
 * enterprise administrators currently use users/{uid}
 * as the canonical fallback authority.
 */
...(inviteAccessScope
  ? {
      accessScope:
        inviteAccessScope,
    }
  : {}),

...(
  Object.keys(
    invitePermissionOverrides
  ).length > 0
    ? {
        permissions:
          invitePermissionOverrides,
      }
    : {}
),

disabled: false,

    archived: false,
    archivedAt: null,
    archivedBy: "",
    archivedReason: "",
    archiveReason: "",

    ...accountState,

    updatedAt:
      serverTimestamp(),
  };

  if (
    finalFacilityId
  ) {
    userPayload.facilityId =
      finalFacilityId;

    userPayload.primaryFacilityId =
      finalFacilityId;
  }

  if (
    finalFacilityIds.length > 0
  ) {
    userPayload.facilityIds =
      finalFacilityIds;

    userPayload.allowedFacilityIds =
      finalFacilityIds;
  }

  if (
    finalCredentials.length > 0
  ) {
    userPayload.credentials =
      finalCredentials;
  }

  if (!userSnap.exists()) {
    userPayload.createdAt =
      serverTimestamp();
  }

  if (
    isWorkerRole(inviteRole) &&
    !userSnap.exists()
  ) {
    userPayload.credentialMap = {};
  }

  /* =====================================================
     5A. WRITE / REPAIR USER PROFILE
     ===================================================== */

  console.log(
    "INVITE REPAIR STAGE 1 — USER WRITE START",
    {
      uid,
      email: emailLower,
      role: inviteRole,
      tenantId: inviteTenantId,
      facilityId: finalFacilityId,
      facilityIds: finalFacilityIds,
      inviteDocId,
    }
  );

  try {
    await setDoc(
      userRef,
      userPayload,
      { merge: true }
    );

    console.log(
      "INVITE REPAIR STAGE 1 — USER WRITE SUCCESS"
    );
  } catch (stageError) {
    console.error(
      "INVITE REPAIR STAGE 1 — USER WRITE FAILED",
      {
        code: stageError?.code,
        message: stageError?.message,
      }
    );

    throw stageError;
  }

  /* =====================================================
     6. CREATE / REPAIR OPERATIONAL ACCESS
     ===================================================== */

  console.log(
    "INVITE REPAIR STAGE 2 — ACCESS PROFILE START",
    {
      uid,
      role: inviteRole,
      tenantId: inviteTenantId,
      facilityId: finalFacilityId,
      facilityIds: finalFacilityIds,
      inviteDocId,
    }
  );

  let accessResult = null;

  try {
    accessResult =
      await finalizeOperationalAccess({
        inviteStatus,

        uid,
        email: emailLower,

        role: inviteRole,

        tenantId:
          inviteTenantId,

        facilityId:
          finalFacilityId,

        facilityIds:
          finalFacilityIds,

        credentials:
          finalCredentials,

        inviteDocId,

        existing: {
          ...existing,
          ...accountState,
        },
      });

    console.log(
      "INVITE REPAIR STAGE 2 — ACCESS PROFILE SUCCESS",
      accessResult
    );
  } catch (stageError) {
    console.error(
      "INVITE REPAIR STAGE 2 — ACCESS PROFILE FAILED",
      {
        code: stageError?.code,
        message: stageError?.message,
      }
    );

    throw stageError;
  }

  /* =====================================================
     7. CLAIM INVITE ONLY AFTER ACCOUNT WORK SUCCEEDS
     ===================================================== */

  console.log(
    "INVITE REPAIR STAGE 3 — INVITE CLAIM START",
    {
      inviteDocId,
      uid,
    }
  );

  try {
    await markInviteRegistered({
      inviteDocId,

      uid,

      role:
        inviteRole,

      tenantId:
        inviteTenantId,

      facilityId:
        finalFacilityId,

      facilityIds:
        finalFacilityIds,
    });

    console.log(
      "INVITE REPAIR STAGE 3 — INVITE CLAIM SUCCESS"
    );
  } catch (stageError) {
    console.error(
      "INVITE REPAIR STAGE 3 — INVITE CLAIM FAILED",
      {
        code: stageError?.code,
        message: stageError?.message,
      }
    );

    throw stageError;
  }

  /* =====================================================
     RESULT
     ===================================================== */

  return {
    applied: true,

    inviteId:
      inviteDocId,

    role:
      inviteRole,

    tenantId:
      inviteTenantId,

    facilityId:
      finalFacilityId,

    facilityIds:
      finalFacilityIds,

    operationalAccessProvisioned:
      Boolean(accessResult),

    accountType:
      isWorkerRole(inviteRole)
        ? "worker"
        : isFacilityOperationalRole(
            inviteRole
          )
        ? "facility_operational"
        : "platform",

    note: userSnap.exists()
      ? "synchronized-existing-user"
      : "created-new-user",
  };
}
